import * as React from "react";
import { Tabs, Tab, Container, Grid, makeStyles, createStyles, Theme } from "@material-ui/core";
import ReportFilter from "../../Report/ReportFilter";
import { useHubx } from "../../../HubX";
import { useParams, useHistory } from "react-router-dom";
import { FETCH_CACHEABLE_CUSTOMERS, FETCH_INSTALLATION_GROUP } from "../../../Fedx";
import useConstructor from "../../../core/hooks/useConstructor";
import get from "lodash/get";
import useAuth from "../../../core/hooks/useAuth";
import CustomerModel from "../../../core/models/Customer";
import ReportLevels from "../ReportLevels";
import ReportConsumptions from "../ReportConsumptions";
import { SessionCache } from "../../../helpers/cache";
import User from "../../../core/models/User";
import WMLogoOneLiner from "../../../assets/icons/WMLogoOneLiner";
import { indexOf } from "lodash";
import { formatDateTime } from "../../../helpers/datetime";
import { SelectOptions } from "../../../core/models/SelectOptions";
import SmartSelectBaseFilter from "../../../core/models/SmartSelectBaseFilter";
import { uniqBy } from "lodash";
import { INSTALLATION_LEVEL_STATUS_TYPES } from "../../../core/models/LevelStatus";
import { getLevelStatusString } from "../../../helpers/installationTankHelpers";
import { isArrayValid } from "../../../helpers/utility";
import {
  PermissionsType,
  PERMISSION_ACCESS_CONSUMPTION_REPORT,
  PERMISSION_ACCESS_LEVEL_REPORT,
} from "../../../core/models/Permission";
import InstallationGroup from "../../../core/models/InstallationGroup";
import { TITLE_PAGE } from "../../../core/constants/appGlobals";
import GoogleAnalyticsService from "../../../services/GoogleAnalyticsService";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tabsWrapper: {
      "paddingTop": theme.spacing(2),
      "display": "flex",
      "alignItems": "flex-start",
      "columnGap": theme.spacing(1.5),

      // home icon
      "& > *:first-child": {
        paddingTop: 4,
      },
    },

    tabsRoot: {
      "borderBottom": `1px solid ${theme.customPalette.border.gray}`,
      "marginBottom": theme.spacing(3),
      "flex": 1,

      "minHeight": theme.spacing(4),

      "& .MuiTab-root": {
        minHeight: theme.spacing(4),
      },
    },

    main: {
      "marginTop": theme.spacing(3),
      "justifyContent": "space-between",
      "columnGap": theme.spacing(2),

      "& .hidden": {
        display: "none",
      },

      "& .main-component": {
        flex: 1,
        maxWidth: "100%",
      },

      "& .information": {
        flexShrink: 1,
      },

      "& .tab-content": {
        "height": "100%",

        "& > *": {
          height: "100%",
        },
      },
    },

    menuWrapper: {
      "display": "flex",
      "flex": 1,
      "justifyContent": "space-between",
      "alignItems": "center",
      "height": "60px",
      "paddingLeft": theme.spacing(2),
      "backgroundColor": theme.palette.background.default,

      "& h4": {
        [theme.breakpoints.up("sm")]: {
          paddingLeft: theme.spacing(12),
        },
        [theme.breakpoints.down("sm")]: {
          paddingLeft: theme.spacing(1),
        },
        fontWeight: "bold",
      },
    },

    flexWrapper: {
      "display": "flex",
      "flexDirection": "column",
      "& .label": {
        fontSize: "1.25rem",
        fontWeight: "bold",
        [theme.breakpoints.down("xs")]: {
          fontSize: "1rem",
        },
      },

      "& .value": {
        "fontSize": "0.8rem",
        "color": theme.palette.grey[700],
        [theme.breakpoints.down("xs")]: {
          fontSize: "0.5rem",
        },
        "width": "max-content",
        "display": "flex",
        "alignItems": "flex-start",

        "& svg": {
          marginLeft: theme.spacing(0.25),
        },
      },
    },

    wmLogo: {
      color: theme.palette.primary.main,
      [theme.breakpoints.down("sm")]: { width: "218px" },
      [theme.breakpoints.up("sm")]: { width: "318px" },
    },
  })
);

export const tabLevelChartLabel = "Níveis";
export const tabConsumptionChartLabel = "Consumo";

const tabs = [tabLevelChartLabel, tabConsumptionChartLabel] as const;

const getTabValueFromKey = (key: "level" | "consumption") => {
  const dic = {
    level: "Níveis",
    consumption: "Consumo",
  };

  return dic[key];
};

function ReportsMain() {
  const classes = useStyles();
  const params = useParams();
  const history = useHistory();
  const authProvider = useAuth();

  const hub = useHubx();

  const selectTabFromRoute = React.useCallback((): number => {
    const initialTabFromRoute: "level" | "consumption" = get(params, "initial_tab");
    const index = indexOf(tabs, getTabValueFromKey(initialTabFromRoute));

    if (index >= 0) {
      return index;
    }

    history.replace("/reports/level/");
    return 0;
  }, [history, params]);

  const statusFilterOptions: SelectOptions[] = INSTALLATION_LEVEL_STATUS_TYPES.map((status) => {
    return {
      label: getLevelStatusString(status),
      value: status,
    };
  });

  const [currentTab, setCurrentTab] = React.useState<number>(() => {
    const initialState = selectTabFromRoute();
    return initialState;
  });
  const [customers, setCustomers] = React.useState<CustomerModel[] | undefined>(undefined);
  const [loading, setLoading] = React.useState(false);
  const [selectedCustomers, setSelectedCustomers] = React.useState<SelectOptions[] | null>(null);
  const [selectedInstallations, setSelectedInstallations] = React.useState<SelectOptions[] | null>(
    null
  );
  const [selectedStatus, setSelectedStatus] = React.useState<SelectOptions[] | null>(
    statusFilterOptions
  );

  React.useMemo(() => {
    const titles: { [key: number]: string } = {
      0: "Nível",
      1: "Consumo",
    };

    const fullTitle = `${TITLE_PAGE}${" - Relatório de " + titles[currentTab]}`;

    document.title = fullTitle;
  }, [currentTab]);

  React.useEffect(() => {
    const newIndex = selectTabFromRoute();
    setCurrentTab(newIndex);
  }, [params, selectTabFromRoute]);

  const userOnCache = React.useMemo(() => {
    const StorageCache = new SessionCache();
    const user: User | null = StorageCache.get("user");
    return user;
  }, []);

  const userLogged = React.useMemo(() => {
    return userOnCache?.name;
  }, []);

  useConstructor(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const APIResponse: CustomerModel[] = await hub.request(FETCH_CACHEABLE_CUSTOMERS);
        setLoading(false);
        setCustomers(APIResponse);
      } catch {
        setLoading(false);
      }
    };

    fetchData();
  });

  const handleChange = (_: React.ChangeEvent<unknown>, newValue: number) => {
    setSelectedCustomers(null);
    setSelectedInstallations(null);
    setCurrentTab(newValue);
    new GoogleAnalyticsService().sendPageViewEvent();
  };

  const getTabPermissionFromLabel = (label: typeof tabs[number]) => {
    switch (label) {
      case tabLevelChartLabel:
        return PERMISSION_ACCESS_LEVEL_REPORT;
      case tabConsumptionChartLabel:
        return PERMISSION_ACCESS_CONSUMPTION_REPORT;
    }
  };

  const permissionToAccess = React.useMemo(() => {
    const permissions: Record<string, PermissionsType> = {
      0: PERMISSION_ACCESS_LEVEL_REPORT,
      1: PERMISSION_ACCESS_CONSUMPTION_REPORT,
    };
    return permissions[currentTab];
  }, [currentTab]);

  const permitedGroupIds = React.useMemo(() => {
    if (customers && permissionToAccess) {
      return customers.map((x) => {
        const hasPermission = authProvider.hasPermission(permissionToAccess, x);
        if (hasPermission) {
          return x.customer_id;
        }
      });
    }
  }, [customers, permissionToAccess, currentTab]);

  const buildFilterFromCustomers: SmartSelectBaseFilter | undefined = React.useMemo(() => {
    if (customers) {
      const options = customers
        .filter((e) => permitedGroupIds?.includes(e.customer_id))
        .map((x) => {
          return {
            value: x.customer_id,
            label: x.customer_name,
          };
        });

      return {
        value: selectedCustomers,
        onChange: setSelectedCustomers,
        options,
      };
    }
  }, [customers, permissionToAccess, permitedGroupIds, selectedCustomers]);

  const getSelectInstallationLabel = React.useCallback((option: SelectOptions) => {
    if (option.name) {
      return `${option.name} ("#Número White: "${option.label})`;
    }
    return option.label;
  }, []);

  const userTabs = React.useMemo(() => {
    if (customers) {
      return tabs.filter((tab) => {
        const permission = getTabPermissionFromLabel(tab);
        return authProvider.hasAnyPermission([permission], customers);
      });
    }
    return [];
  }, [tabs, currentTab, customers]);

  const filterCustomers = React.useMemo(() => {
    const selectedCustomersIds = selectedCustomers?.map((x) => x.value) ?? [];
    const ret = customers?.filter((x) => selectedCustomersIds.includes(x.customer_id)) ?? [];
    return ret;
  }, [customers, selectedCustomers]);

  const filterCustomersRadix = React.useMemo(() => {
    const selectedCustomersIds = selectedCustomers?.map((x) => x.value) ?? [];

    const ret: InstallationGroup[] | undefined =
      userOnCache?.installationGroup?.filter((x) =>
        selectedCustomersIds.includes(x.id.toString() as string)
      ) ?? [];
    return ret;
  }, [customers, selectedCustomers]);

  const filteredInstallations = React.useMemo(() => {
    const installations = filterCustomers?.flatMap((customer) => customer.installations) ?? [];

    if (installations) return uniqBy(installations, (x) => x?.installation_id) || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomers]);

  const filteredRadixInstallations = React.useMemo(() => {
    const installations =
      filterCustomersRadix?.flatMap((customer) => customer.installationList) ?? [];

    if (installations) return uniqBy(installations, (x) => x?.installationId) || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomers]);

  const filterFromInstallation: SmartSelectBaseFilter | undefined = React.useMemo(() => {
    if (isArrayValid(filteredInstallations)) {
      const filteredRadix = filteredRadixInstallations;
      const options: SelectOptions[] =
        filteredInstallations?.map((installation) => {
          const radixInstallation = filteredRadix?.find(
            (x) => x.installationId == installation?.installation_id
          );

          return {
            label: installation?.installation_id ?? "",
            name: radixInstallation?.name || installation?.name || "",
            value: installation?.installation_id ?? "",
          };
        }) || [];

      return {
        onChange: setSelectedInstallations,
        options,
        value: selectedInstallations,
        getOptionLabel: getSelectInstallationLabel,
      };
    }
  }, [filteredInstallations, getSelectInstallationLabel, selectedInstallations]);

  const statusFilter: SmartSelectBaseFilter = React.useMemo(() => {
    return {
      onChange: setSelectedStatus,
      value: selectedStatus,
      options: statusFilterOptions,
    };
  }, [selectedStatus, statusFilterOptions]);

  const selectedInstallationsIds = selectedInstallations?.map((x) => x.value) ?? [];

  const selectedStatusArr =
    selectedStatus?.map((x) => x.value.toLocaleLowerCase()).filter((x) => x) ?? [];

  const filteredInstallationsIds =
    filteredInstallations
      ?.map((x) => x?.installation_id ?? "")
      ?.filter((x) => x)
      ?.filter((x) => selectedInstallationsIds.includes(x)) || [];

  return (
    <Container component="main" maxWidth="xl">
      <div className={classes.tabsWrapper}>
        <Tabs
          className={classes.tabsRoot}
          value={currentTab}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          aria-label="installation-tabs-navigation-scrollable"
        >
          {userTabs.map((tab, idx) => (
            <Tab key={`installation-tab-${tab}-${idx}`} label={tab} />
          ))}
        </Tabs>
      </div>

      <ReportFilter
        installationFilter={filterFromInstallation}
        customerFilter={buildFilterFromCustomers}
        isLoading={loading}
        selectedTab={currentTab}
        statusFilter={statusFilter}
      />

      <Grid className={classes.main} container>
        <Grid item xs="auto" className={"main-component"}>
          {/* Change content based on tab value */}
          <div>
            <div className={classes.menuWrapper}>
              <div className={classes.flexWrapper}>
                <div className="label">
                  {currentTab === 0
                    ? "Relatório de Níveis"
                    : currentTab === 1
                    ? "Relatório de Consumo"
                    : ""}
                </div>
                <div className="value">
                  Emitido por {userLogged} em {formatDateTime(new Date())}
                </div>
              </div>
              <WMLogoOneLiner className={classes.wmLogo} />
            </div>
            {currentTab === 0 && (
              <ReportLevels
                filteredCustomers={filterCustomers}
                filteredInstallationsIds={filteredInstallationsIds}
                statusFilter={selectedStatusArr}
              />
            )}
            {currentTab === 1 && (
              <ReportConsumptions
                filteredCustomers={filterCustomers}
                filteredInstallationsIds={filteredInstallationsIds}
                statusFilter={selectedStatusArr}
              />
            )}
          </div>
        </Grid>
      </Grid>
    </Container>
  );
}

export default ReportsMain;
